import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StorageService, StorageType, TranslationService } from 'gast-common-ng2';
import { ClozeTestComponent, ClozeTestModel, ClozeTestStyleModel, UrlParametersModel } from 'gast-onset-ng2';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { BookingMappingDto } from '../../shared/dtos/booking-mapping-dto';
import { BookingDto } from '../../shared/dtos/booking/booking-dto';
import { StorageKeys } from '../../shared/services/constants/storage-keys';
import { OnsetService } from '../../shared/services/onset.service';

@Component({
  selector: 'app-onset-study',
  templateUrl: './onset-study.component.html',
  styleUrls: ['./onset-study.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ClozeTestComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OnsetStudyComponent implements OnInit {
  private readonly envConfig = inject<any>('env' as any);
  readonly #deviceService = inject(DeviceDetectorService);
  readonly #onsetService = inject(OnsetService);
  readonly #storageService = inject(StorageService);
  readonly translationService = inject(TranslateService);
  readonly translateService = inject(TranslationService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);

  environment = this.envConfig.callback(this.envConfig.value);
  mobileMode = signal(false);

  lang = signal('DE');

  subscription = new Subscription();

  cTestLogo = signal('');
  clozeTestModel = signal<ClozeTestModel>({} as ClozeTestModel);
  clozeTestStyleModel = signal<ClozeTestStyleModel>({} as ClozeTestStyleModel);

  currentBooking = signal<BookingDto | null>(null);

  username = signal('');
  enoughMobileParticipant = signal(false); // 100 is cut off
  cTestButtonColor = signal('');

  bookingLoaded = signal(false);
  participantsCounted = signal(false);
  onsetStarted = signal(false);

  startLevel = signal(2);
  endLevel = signal(4);
  mobileVariantId = signal(13);
  desktopVariantId = signal(14);
  language = signal(1);

  bookingMappings = signal<Array<BookingMappingDto>>([]);
  mobileCtestDone = signal(false);
  desktopCtestDone = signal(false);

  frontEndLanguage = signal(1);

  displayStatus = signal('');

  showCustomButton = signal(true);
  customButtonIcon = signal('assets/Notizblock.png');
  customButtonIconHeight = signal('60px');
  customButtonText = signal(' Zum Fragebogen');

  deLevelText = 'Das Testergebnis erlaubt eine Einstufung analog zur globalen Skala des Gemeinsamen europäischen Referenzrahmens bis maximal B2 oder höher. Die Einstufung lautet:';
  enLevelText = 'The test result allows a classification according to the global scale of the Common European Framework of Reference up to B2 or higher. The classification is:';
  customResultLevelText = signal(this.deLevelText);

  showIntroduction = signal(true);
  canGoToSurveyDesktop = signal(false);
  canGoToSurveyMobile = signal(false);

  ngOnInit() {
    // detect mobile
    this.mobileMode.set(this.#deviceService.isMobile());

    // handle language
    this.#route.queryParams.subscribe(params => {
      if (params['lang']) {
        const lang = params['lang'].toLocaleLowerCase();
        this.lang.set(lang.toLocaleUpperCase());
        this.changeLanguage(lang);
      }
    });

    // this.mobileMode = true;
    this.clozeTestStyleModel.set(this.#generateClozeTestStyle());
    this.cTestButtonColor.set(this.clozeTestStyleModel()!.onsetColor);
    // sett logo and button color
    this.cTestLogo.set('onset-assets/img/onset_logo.png');
    // get username from storage
    const user = this.#storageService.getItem(StorageKeys.USER, StorageType.Local);
    if (user) {
      this.username.set((user as any).username);
    }

    this.subscription.add(this.#onsetService.bookingMappingSaved.subscribe((bookingMappings: Array<BookingMappingDto>) => {
      this.bookingMappings.set(bookingMappings);
      // set status for each onset type
      this.bookingMappings().forEach(bm => {
        if (bm.onsetType == 'DESKTOP' && bm.bookingStatus && bm.bookingStatus == 'COMPLETED') {
          this.desktopCtestDone.set(true);
          this.canGoToSurveyDesktop.set(true);
        }
        if (bm.onsetType == 'MOBILE' && bm.bookingStatus && bm.bookingStatus == 'COMPLETED') {
          this.mobileCtestDone.set(true);
          this.canGoToSurveyMobile.set(true);
        }
      });
      this.bookingLoaded.set(true);
      this.#onsetService.checkIfEnoughMobileParticipantForOnsetStudy();
    }));

    this.subscription.add(this.#onsetService.bookingStudyLoaded.subscribe((bookingDto: BookingDto) => {
      this.currentBooking.set(bookingDto);
      // get or create onset booking mapping (onsetDb)
      // get or create onset booking (onsetDb)
      this.#onsetService.createBookingMappingForBooking(bookingDto, this.username());
    }));

    this.subscription.add(this.#onsetService.checkIfEnoughMobileParticipantDone.subscribe((enoughParticipant: boolean) => {
      this.enoughMobileParticipant.set(enoughParticipant);
      this.participantsCounted.set(true);
      // determine what to display
      if (this.mobileCtestDone() && this.desktopCtestDone() && this.mobileMode()) {
        this.displayStatus.set('SHOW_MOBILE');
      } else if (this.mobileCtestDone() && this.desktopCtestDone() && !this.mobileMode()) {
        this.displayStatus.set('SHOW_DESKTOP');
      } else if ((!this.enoughMobileParticipant() || this.desktopCtestDone()) && !this.mobileMode() && !this.mobileCtestDone()) {
        this.displayStatus.set('SHOW_MOBILE_WARNING');
      } else if ((!this.enoughMobileParticipant() || this.desktopCtestDone()) && this.mobileMode() && !this.mobileCtestDone()) {
        this.displayStatus.set('SHOW_MOBILE');
      } else if ((this.enoughMobileParticipant() || this.mobileCtestDone()) && this.mobileMode() && !this.desktopCtestDone()) {
        this.displayStatus.set('SHOW_DESKTOP_WARNING');
      } else if ((this.enoughMobileParticipant() || this.mobileCtestDone()) && !this.mobileMode() && !this.desktopCtestDone()) {
        this.displayStatus.set('SHOW_DESKTOP');
      }
    }));

    this.subscription.add(this.translationService.onLangChange.subscribe((l) => {
      switch (l.lang) {
        case 'de':
          this.customResultLevelText.set(this.deLevelText);
          break;
        case 'en':
          this.customResultLevelText.set(this.enLevelText);
          break;
        default:
          this.customResultLevelText.set(this.deLevelText);
          break;
      }

      if (l.lang.toLocaleUpperCase() == 'de') {
        this.frontEndLanguage.set(1);
      } else {
        this.frontEndLanguage.set(2);
      }
    }));

    // get current onset-booking-study (bookingDb) or create new if not exist
    this.#onsetService.createOnsetBookingStudy();
  }

  changeLanguage(lang: string) {
    this.translateService.changeLanguageUsingCode(lang);
    if (lang.toLocaleUpperCase() == 'DE') {
      this.frontEndLanguage.set(1);
    } else {
      this.frontEndLanguage.set(2);
    }

    this.#router.navigate(
      [],
      {
        relativeTo: this.#route,
        queryParams: { lang: lang.toLocaleLowerCase() },
        queryParamsHandling: 'merge'
      });
  }

  startMobileOnset(): void {
    const bookingMapping = this.bookingMappings().filter(bm => bm.onsetType == 'MOBILE')[0];
    this.clozeTestModel.set(this.#generateClozeTestModel(bookingMapping.bookingId));
    this.onsetStarted.set(true);
  }

  startDesktopOnset(): void {
    const bookingMapping = this.bookingMappings().filter(bm => bm.onsetType == 'DESKTOP')[0];
    this.clozeTestModel.set(this.#generateClozeTestModel(bookingMapping.bookingId));
    this.onsetStarted.set(true);
  }

  updatedCustomButtonText(): void {
    if (this.lang() === 'DE') {
      this.customButtonText.set(' Zum Fragebogen');
    } else {
      this.customButtonText.set(' To the questionnaire');
    }
  }

  onStartTest(_start: boolean): void {
    console.log(`test started`);
  }

  onBookingInit(gastBookingId: number): void {
    console.log(`init gastBookingId ${gastBookingId}`);
  }

  onTestHasBeenSaved(gastBookingId: number): void {
    console.log(`saving gastBookingId ${gastBookingId}`);
  }

  onClozeTestCompleted(): void {
    console.log(`test completed`);
  }

  onTestResult(test: any): void {
    console.log(`test result ${test}`);
  }

  toLimeSurvey(): void {
    const os = this.#deviceService.os_version;
    const browser = this.#deviceService.browser;
    const device = this.#deviceService.device;
    const width = window.screen.width;
    const height = window.screen.height;
    const resolution = `${width.toString()}x${height.toString()}`;
    const url = `https://umfragen.gast.de/index.php/664968?C1=${device}&C2=${os}&C3=${browser}&C4=${resolution}`;
    window.open(url, "_blank");
  }

  onGerInfoClicked(): void {
    let lang = 'de';
    if (this.frontEndLanguage() == 2) {
      lang = 'en'
    }
    const url = this.#router.serializeUrl(
      this.#router.createUrlTree(['/ger-info'], { queryParams: { lang: lang } })
    );
    window.open(url, '_blank');
  }

  #generateClozeTestModel(gastBookingId: number): ClozeTestModel {
    const onsetServiceUrl = this.environment.onsetServiceUrl;
    // const onsetServiceUrl = 'http://gast-appr-app-2:8008/gast-service-onset/';
    // const onsetServiceUrl = 'https://apprex.gast.de/gast-service-onset/';

    let variantId = 13;
    if (!this.mobileMode()) {
      variantId = 14;
    }

    // TODO config
    const clozeTestModel: ClozeTestModel = {} as ClozeTestModel;
    clozeTestModel.getResultsUrl = onsetServiceUrl + 'api/score';
    clozeTestModel.postTestUrl = onsetServiceUrl + 'api/aufgabe/save';
    clozeTestModel.getTestUrl = onsetServiceUrl + 'api/aufgabe';
    clozeTestModel.getNextTestUrl = onsetServiceUrl + 'api/aufgabe/next';
    clozeTestModel.startWithBuchungId = gastBookingId;
    clozeTestModel.onsetServiceUrl = onsetServiceUrl;
    clozeTestModel.frontEndLanguageId = this.frontEndLanguage();
    clozeTestModel.urlParameters = {
      herkunft: 'deutschland',
      geschlecht: 'm',
      geburtsdatum: '1990-01-01',
      variante: variantId,
      testzentrum: 454,
      sprache: this.language(),
      tool: 1,
      start_level: this.startLevel(),
      end_level: this.endLevel()
    } as UrlParametersModel;
    return clozeTestModel;
  }

  #generateClozeTestStyle(): ClozeTestStyleModel {
    // TODO config
    const model = {} as ClozeTestStyleModel;
    model.siteBackground = '#ffffff';
    model.exerciseBackground = '#EEF3F8';
    model.frameColor = '#92aac1';
    model.barColor = '#b3c9de';
    model.specialCharacterListColor = '#b3c9de';
    model.specialChcaracterTextColor = '#000000';
    model.exerciseTextColor = '#000000';
    model.buttonsColor = '#fa9463';
    model.testdafColor = '#57a7d6';
    model.onsetColor = '#a5d8db';
    model.duoColor = '#ed6a5b';
    model.testasColor = '#fac075';
    model.font = 'arial, helvetica, sans-serif';
    model.fontSize = '11pt';
    model.displayLogo = true;
    model.cTestInformationUrl = 'https://www.onset.de/onset/durchfuehrung/zusatz.jsp';
    return model;
  }
}
